import { useEffect, useId, useRef, useState } from "react";
import { FORWARD_NOTE_MAX, forwardError, forwardRecipients, noteCountLabel } from "../email";
import type { AuthorityCurrency, ObservedView, PlayerAction } from "../types";
import { CURRENCIES } from "./InstructionsPanel";

/**
 * Forward an email to someone to deal with (21 §4d). It is a delegation: a note makes it clearer, and you can
 * only hand over authority you hold. Nothing here warns about who you choose; what follows is the lesson.
 */
export function ForwardForm({ instanceId, view, busy, onSend, onCancel }: {
  instanceId: string; view: ObservedView; busy: boolean;
  onSend: (status: string, a: PlayerAction) => void; onCancel: () => void;
}) {
  const ids = useId();
  const [personId, setPersonId] = useState("");
  const [note, setNote] = useState("");
  const [currencies, setCurrencies] = useState<AuthorityCurrency[]>([]);
  const [error, setError] = useState<string | null>(null);
  const select = useRef<HTMLSelectElement>(null);
  const recipients = forwardRecipients(view.people, view.you.id);
  const authority = view.you.authority;
  useEffect(() => { select.current?.focus(); }, []);

  const toggle = (c: AuthorityCurrency) => setCurrencies((cs) => cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]);

  const submit = () => {
    const problem = forwardError(personId, note);
    setError(problem);
    if (problem) {
      if (!personId) select.current?.focus();
      return;
    }
    const to = recipients.find((p) => p.id === personId);
    const trimmed = note.trim();
    onSend(`Forwarded to ${to?.name ?? "them"}.`, {
      type: "forward", instanceId, personId,
      currencies: currencies.filter((c) => authority[c] !== "none"),
      ...(trimmed ? { note: trimmed } : {}),
    });
  };

  const personInvalid = error !== null && !personId;
  const noteInvalid = note.length > FORWARD_NOTE_MAX;
  return (
    <form className="forward" aria-labelledby={`${ids}-title`} noValidate onSubmit={(e) => { e.preventDefault(); submit(); }}
      onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onCancel(); } }}>
      <h4 id={`${ids}-title`} className="forward__title">Forward this email</h4>
      {error && <p id={`${ids}-error`} className="form__error" role="alert">{error}</p>}
      <label htmlFor={`${ids}-to`}>Forward to</label>
      <select id={`${ids}-to`} ref={select} value={personId} required
        aria-invalid={personInvalid || undefined} aria-describedby={personInvalid ? `${ids}-error` : undefined}
        onChange={(e) => { setPersonId(e.target.value); if (error) setError(null); }}>
        <option value="">Choose someone…</option>
        {recipients.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.role})</option>)}
      </select>
      <label htmlFor={`${ids}-note`}>Note <span className="muted">(optional)</span></label>
      <textarea id={`${ids}-note`} rows={3} value={note} maxLength={FORWARD_NOTE_MAX}
        aria-invalid={noteInvalid || undefined} aria-describedby={`${ids}-count`}
        onChange={(e) => { setNote(e.target.value); if (error) setError(null); }}
        placeholder="What you want them to do with it" />
      <p id={`${ids}-count`} className="muted small forward__count">{noteCountLabel(note)}</p>
      <fieldset className="forward__currencies">
        <legend>With your authority to</legend>
        {CURRENCIES.map((c) => (
          <label key={c.id} className="check">
            <input type="checkbox" checked={currencies.includes(c.id)} onChange={() => toggle(c.id)} disabled={authority[c.id] === "none"} />
            <span><strong>{c.label}</strong> <span className="muted">— {authority[c.id] === "none" ? "you don't hold this authority yourself, so you can't hand it over" : c.hint}</span></span>
          </label>
        ))}
      </fieldset>
      <div className="row-actions">
        <button type="submit" className="btn btn--primary" disabled={busy}>Forward</button>
        <button type="button" className="btn btn--quiet" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}
